// ============================================================
// EffectParts - the Effects tab's bundles (plan modes-rework, stage 4):
//
//   ALWAYS ON   - the unnamed always-on list, edited in place; then each
//                 Always on item (a trigger says what it waits on)
//   SOLO MODES  - each one-at-a-time group, then the free ones
//
// Each bundle a row: Edit (its bench), Update when the library holds a
// newer version of the one it was copied from, Remove. Each part adds
// "+ New here" (the bench on a blank bundle) and, where a library is given
// (the character editor, on the Assets page and at the table), "+ From
// library" (a copy with its origin; an item already copied is refreshed by
// its row's Update, never copied twice). The library editor's Effects tab and the session
// sheet's Effects section both show it; what each adds (innate exposures,
// the saved condition, Squad Mode) comes in as `alwaysExtra` and after it.
// ============================================================
import { useState, type ReactNode } from 'react';
import type { Character } from '../../engine/character/types';
import type { CombatState } from '../../engine/roster/types';
import { readEffects, type StanceEffect } from '../../engine/character/effectItems';
import { OWN_STATE_CHOICES } from '../../engine/character/effectWhen';
import { soloGroups, type EffectItem, type SoloMode } from '../../engine/character/soloModes';
import { newerOf, withCopy, withoutBundle, type Offered } from '../../engine/character/bundleCopies';
import type { LibraryBundle } from '../../hooks/useLibraryContent';
import { mintUnique } from '../../utils/mintUnique';
import common from '../../styles/common.module.css';
import styles from '../../styles/Stances.module.css';
import EffectList from '../stances/EffectList';
import EffectItemBench from './EffectItemBench';

type Kind = 'alwaysOn' | 'soloMode';
type Bundle = EffectItem | SoloMode;
/** The library's bundles, as the Effects tab copies from them. */
export type LibraryBundles = readonly LibraryBundle[];
/** What the parts write: the two lists and the always-on list. */
type EffectChange = Partial<Pick<Character, 'alwaysOn' | 'soloModes' | 'permanentEffects'>>;

interface Props {
  character: Character;
  onChange: (partial: EffectChange) => void;
  locked?: boolean;
  /** Where the bench says it is: "Brother Vael · sheet", "My library · Brother Vael". */
  where: string;
  /** What else is always on, below the bundles (innate exposures). */
  alwaysExtra?: ReactNode;
  /** The library's bundles; absent or null, there is nothing to copy from. */
  library?: LibraryBundles | null;
  /** Opened from a sheet in play: the bench offers "Back to the table". */
  atTable?: boolean;
  /** The combatant's live state, in play: the bench judges a condition about oneself on it. */
  state?: CombatState | null;
}

const FIELD: Record<Kind, 'alwaysOn' | 'soloModes'> = { alwaysOn: 'alwaysOn', soloMode: 'soloModes' };
const TITLES: Record<Kind, string> = { alwaysOn: 'Always on', soloMode: 'Solo mode' };
/** A bundle made here: an id of its own, minted when asked for, and no name until the bench gives it one. */
const blankOf = (prefix: string): Bundle => ({ id: `${prefix}-${mintUnique()}`, name: '', effects: [] });
const stateLabel = (id: string) => OWN_STATE_CHOICES.find((s) => s.id === id)?.label ?? id;

/** One kind's list, and the edits each part makes to it - each written as a whole list. */
function useList(kind: Kind, character: Character, onChange: Props['onChange']) {
  const list: Bundle[] = [...((kind === 'alwaysOn' ? character.alwaysOn : character.soloModes) ?? [])];
  const write = (next: Bundle[]) => onChange({ [FIELD[kind]]: next });
  const upsert = (b: Bundle) =>
    write(list.some((x) => x.id === b.id) ? list.map((x) => (x.id === b.id ? b : x)) : [...list, b]);
  return {
    list,
    upsert,
    remove: (id: string) => write(withoutBundle(list, id)),
    copy: (item: Offered<Bundle>) => write(withCopy(list, item)),
  };
}

type List = ReturnType<typeof useList>;

/** "up while Prone", "from the library, v2 (v3 is newer)". */
function Says({ bundle, newer }: { bundle: Bundle; newer: Offered<Bundle> | null }) {
  const trigger = 'whileOwn' in bundle && bundle.whileOwn ? `up while ${stateLabel(bundle.whileOwn)}` : null;
  const origin = bundle.origin
    ? `from the library, v${bundle.origin.version}${newer ? ` (v${newer.asset.version} is newer)` : ''}`
    : null;
  const said = [trigger, origin].filter(Boolean).join(' · ');
  return said ? <span className={styles.says}> · {said}</span> : null;
}

interface RowProps {
  bundle: Bundle;
  newer: Offered<Bundle> | null;
  locked: boolean;
  onEdit: () => void;
  list: List;
}

function BundleRow({ bundle, newer, locked, onEdit, list }: RowProps) {
  return (
    <div className={styles.effectRow} data-testid={`bundle-row-${bundle.id}`}>
      <span>
        {bundle.name}
        <Says bundle={bundle} newer={newer} />
      </span>
      <button type="button" className={common.btnSmall} data-testid={`bundle-edit-${bundle.id}`} onClick={onEdit}>
        {locked ? 'Look' : 'Edit'}
      </button>
      {newer && !locked && (
        <button
          type="button"
          className={common.btnSmall}
          data-testid={`bundle-update-${bundle.id}`}
          title={`Writes the library's v${newer.asset.version} over this copy, edits made here included.`}
          onClick={() => list.copy(newer)}
        >
          Update
        </button>
      )}
      {!locked && (
        <button
          type="button"
          className={common.btnSmall}
          aria-label={`Remove ${bundle.name}`}
          data-testid={`bundle-remove-${bundle.id}`}
          onClick={() => list.remove(bundle.id)}
        >
          Remove
        </button>
      )}
    </div>
  );
}

/** "+ From library" (when there is one) and "+ New here". */
function AddBar({
  kind,
  library,
  list,
  onNew,
}: {
  kind: Kind;
  library: Offered<Bundle>[] | null;
  list: List;
  onNew: () => void;
}) {
  // An item already copied is refreshed by its row's Update: picked again, it would overwrite the copy unasked.
  const fresh = library?.filter((i) => !list.list.some((b) => b.origin?.assetId === i.asset.id)) ?? [];
  const none = library?.length ? 'All copied' : 'None in the library';
  return (
    <div className={styles.row}>
      {library && (
        <select
          className={common.input}
          aria-label={`Add a ${TITLES[kind]} from the library`}
          data-testid={`bundle-pick-${kind}`}
          value=""
          disabled={!fresh.length}
          onChange={(e) => {
            const item = fresh.find((i) => i.asset.id === e.target.value);
            if (item) list.copy(item);
          }}
        >
          <option value="">{fresh.length ? '+ From library…' : none}</option>
          {fresh.map((i) => (
            <option key={i.asset.id} value={i.asset.id}>
              {i.asset.name}
            </option>
          ))}
        </select>
      )}
      <button type="button" className={common.btnSmall} data-testid={`bundle-new-${kind}`} onClick={onNew}>
        + New here
      </button>
    </div>
  );
}

interface PartProps {
  kind: Kind;
  list: List;
  library: Offered<Bundle>[] | null;
  locked: boolean;
  open: (kind: Kind, bundle: Bundle) => void;
}

/** The rows of one list, with what the library holds newer of each. */
function Rows({ bundles, kind, list, library, locked, open }: PartProps & { bundles: readonly Bundle[] }) {
  return (
    <>
      {bundles.map((b) => (
        <BundleRow
          key={b.id}
          bundle={b}
          newer={newerOf(b, library ?? [])}
          locked={locked}
          onEdit={() => open(kind, b)}
          list={list}
        />
      ))}
    </>
  );
}

function AlwaysOnPart(
  p: PartProps & { always: StanceEffect[]; onAlways: (e: StanceEffect[]) => void; extra?: ReactNode },
) {
  return (
    <section className={styles.effectSection} data-testid="effects-always">
      <EffectHeading title="Always on" says="up in Solo and in Squad" />
      <Rows {...p} bundles={p.list.list} />
      {!p.locked && (
        <AddBar kind="alwaysOn" library={p.library} list={p.list} onNew={() => p.open('alwaysOn', blankOf('aon'))} />
      )}
      <span className={styles.says}>Effects with no name of their own:</span>
      <EffectList effects={p.always} onChange={p.onAlways} id="always-on" owner="Always on" locked={p.locked} />
      {p.extra}
    </section>
  );
}

function SoloPart(p: PartProps) {
  const groups = soloGroups(p.list.list as SoloMode[]);
  return (
    <section className={styles.effectSection} data-testid="effects-solo">
      <EffectHeading title="Solo modes" says="each switched on and off; all stand down in Squad Mode" />
      {groups.map((g) => (
        <div key={g.id} role="group" aria-label={g.name}>
          <span className={styles.says}>{g.oneAtATime ? `${g.name} · one at a time` : g.name}</span>
          <Rows {...p} bundles={g.modes} />
        </div>
      ))}
      {!groups.length && <span className={styles.says}>None yet.</span>}
      {!p.locked && (
        <AddBar kind="soloMode" library={p.library} list={p.list} onNew={() => p.open('soloMode', blankOf('solo'))} />
      )}
    </section>
  );
}

/** A part's title and what it means; the Effects tab's other parts use it too. */
export function EffectHeading({ title, says, children }: { title: string; says: string; children?: ReactNode }) {
  return (
    <div className={styles.effectHeading}>
      <h4 className={styles.effectTitle}>{title}</h4>
      <span className={styles.says}>{says}</span>
      {children}
    </div>
  );
}

/** The library's items of one kind, as offers. */
const offersOf = (library: LibraryBundles | null | undefined, kind: Kind): Offered<Bundle>[] | null =>
  library ? library.filter((i) => i.kind === kind).map((i) => ({ asset: i.asset, content: i.content })) : null;

export default function EffectParts(props: Props) {
  const { character, onChange, locked = false, where, alwaysExtra, library, atTable, state } = props;
  const [bench, setBench] = useState<{ kind: Kind; bundle: Bundle } | null>(null);
  const always = useList('alwaysOn', character, onChange);
  const solo = useList('soloMode', character, onChange);
  const open = (kind: Kind, bundle: Bundle) => setBench({ kind, bundle });
  const lists = { alwaysOn: always, soloMode: solo };
  return (
    <div data-testid="effect-parts">
      <AlwaysOnPart
        kind="alwaysOn"
        list={always}
        library={offersOf(library, 'alwaysOn')}
        locked={locked}
        open={open}
        always={readEffects(character.permanentEffects)}
        onAlways={(permanentEffects) => onChange({ permanentEffects })}
        extra={alwaysExtra}
      />
      <SoloPart kind="soloMode" list={solo} library={offersOf(library, 'soloMode')} locked={locked} open={open} />
      {bench && (
        <EffectItemBench
          // One bench per bundle: another opened while one is set aside starts fresh, never on the last one's draft.
          key={`${bench.kind}:${bench.bundle.id}`}
          kind={bench.kind}
          bundle={bench.bundle}
          where={where}
          locked={locked}
          atTable={atTable}
          on={{ sheet: character, state }}
          onClose={() => setBench(null)}
          onSave={(b) => {
            lists[bench.kind].upsert(b);
            setBench(null);
          }}
        />
      )}
    </div>
  );
}
